import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  getOnboardingProfile, getSubscription,
  getCardApplication, submitCardApplication, saveCardDraft, setPostpayHidden, uploadCardFiles,
} from '../api/client';
import {
  Hammer, Clipboard, Building, TrendingUp, Receipt, Scale, Home, Shield,
  MessageCircle, Star, Wrench, Euro, IconBadge,
} from './icons';

// ============================================================================
// 🧪 ПЕСОЧНИЦА — ПОСЛЕОПЛАТНЫЕ СЛАЙДЫ (показываются ПОСЛЕ оплаты, ПЕРЕД входом
// в приложение). Отдельная поверхность от пре-оплатной воронки (OnboardingDev),
// т.к. та гейтится на !subscription_active. Каркас/стиль/анимации зеркалят
// OnboardingDev. 4 слайда: success · welcome · registration (анкета карточки
// fix-hub.de, 4 шага, префилл из дооплатной воронки, автосейв) · estimates.
// Прод (Onboarding.js) и логика оплаты НЕ затронуты — только добавление.
// ============================================================================

// ─── Константы / backend-стабы (заменить при интеграции) ────────────────────
// Данные о покупке для экрана «Оплата прошла»: пакет, срок, дата след. списания.
// TODO: тянуть из данных о покупке (Stripe/подписка); пока — заглушка.
const PURCHASE_INFO = {
  plan: 'Fix-Hub + Platform · Standard',
  termLabel: 'На 12 месяцев',      // срок доступа
  nextBilling: '07.07.2027',       // дата следующего списания; '' если разовая оплата
};

// Параллельно с показом экрана бот шлёт клиенту подтверждение оплаты в Telegram-чат.
// Бот живёт ОТДЕЛЬНО на сервере → здесь только backend-хук-стаб.
// TODO(backend): подключить реальный POST /api/notify-payment { plan, ... }.
// Пока — no-op, чтобы не ломать UX и не бить по несуществующему эндпоинту.
function notifyPayment(meta) {
  try { console.debug('[postpay] notifyPayment stub →', meta); } catch { /* noop */ }
}

// Встроенное «жёлтое видео» / инструкция (тот же слот, что на главной, s.instruction_video).
const INTRO_VIDEO_URL = 'https://www.youtube.com/embed/Xpb4BA2ZL_M?rel=0&modestbranding=1&playsinline=1';
const INTRO_VIDEO_WATCH_URL = 'https://youtu.be/Xpb4BA2ZL_M';

// ─── «Больше не показывать»: статус по каждому ПОВТОРЯЮЩЕМУСЯ слайду отдельно ──
// Отмечен → при следующем входе слайд пропускается; не отмечен → показывается снова.
// Источник истины — профиль (POST /api/card-application/hide), переживает смену устройства.
// localStorage — синхронный фолбэк/кэш (гейт слайдов строится синхронно на маунт).
const HIDEABLE_SLIDES = ['welcome', 'registration'];
const hideKey = (id) => `fixhub_postpay_hide_${id}`;
const isSlideHidden = (id) => {
  try { return localStorage.getItem(hideKey(id)) === '1'; } catch { return false; }
};
// Текущий список скрытых слайдов (из localStorage-кэша).
const hiddenList = () => HIDEABLE_SLIDES.filter(isSlideHidden);
const setSlideHidden = (id, v) => {
  try { if (v) localStorage.setItem(hideKey(id), '1'); else localStorage.removeItem(hideKey(id)); }
  catch { /* noop */ }
  // Зеркалим в профиль (best-effort, не блокируем UX).
  try { setPostpayHidden(hiddenList()).catch(() => {}); } catch { /* noop */ }
};
// Синхронизировать localStorage-кэш скрытых слайдов из профиля (при входе).
function applyHiddenFromServer(hidden) {
  if (!Array.isArray(hidden)) return;
  for (const id of HIDEABLE_SLIDES) {
    try {
      if (hidden.includes(id)) localStorage.setItem(hideKey(id), '1');
      else localStorage.removeItem(hideKey(id));
    } catch { /* noop */ }
  }
}
// Сброс всех песочных флагов (все «больше не показывать» + переключатель смет) — для тестов.

// Открыть внешнюю ссылку через Telegram WebApp (фолбэк — новая вкладка).
function openExternal(url) {
  const tg = window.Telegram?.WebApp;
  if (tg?.openLink) tg.openLink(url); else window.open(url, '_blank');
}

// ─── Слайд 3: анкета fix-hub.de / FIX-CLUB ──────────────────────────────────
// Скриншот платформы fix-hub.de. TODO: заменить на реальную картинку (пришлёт владелец).
// Пусто → рендерим стилизованную заглушку.
const PLATFORM_SCREENSHOT_URL = '/postpay-card-example.jpg'; // живая карточка Exzellentia (qa-shot-card.js)

// Отправка анкеты на бэкенд: POST /api/card-application (upsert, уведомляет менеджеров).
// values.logo (url) и values.gallery ([url]) уже проставлены аплоадером.
function submitRegistration(values) {
  return submitCardApplication(values);
}

// ─── Анкета v3 (план утверждён владельцем 07.07): 12 лёгких экранов + ветвление ──
// 4 типа фирмы → своя ветка: категории, чипы специализаций, примеры, подписи.
// Все тексты веток — в одном объекте BRANCHES (легко править после приёмки).
// Категории сверены с живыми таксономиями: fix-hub.de (Handwerkersuche, 17),
// experten.fix-hub.de (12), shop.fix-hub.de (12) — taxonomy listing_category.

const CATS_BUILD = [
  'Rohbau und Innenausbau', 'Bodenarbeiten und Verlegung', 'Fassaden- und Malerarbeiten',
  'Putz- und Estricharbeiten', 'Fenster- und Türenmontage', 'Bauplanung und Projektbetreuung',
  'Bad- und Heizungsinstallation', 'Möbelbau und Montage', 'Elektrotechnik und Gebäudeverkabelung',
  'Dacharbeiten und Abdichtungssysteme', 'Garten- und Außenanlagen', 'Umzugshilfe & Möbellogistik',
  'Entrümpelung (Privat & Gewerbe)', 'Metallbau und Konstruktionstechnik',
  'Schlüssel- und Hausmeisterdienst', 'Reinigungsservice', 'Notfall-Reparaturen 24/7',
];
const CATS_EXPERT = [
  'Steuerberatung & Buchhaltung', 'Rechtsanwälte & Anwälte',
  'Versicherungsberatung (Gesundheit/Geschäft)', 'Finanzierung & Kreditberatung',
  'Gründungsberatung & Personalberatung', 'Marketing & IT-Beratung',
  'Immobilienmakler & Notare', 'Dolmetscher & Übersetzer',
  'Architekten, Ingenieure & Designer', 'Sachverständige & Tenderberatung',
  'Transport & Logistikdienste', 'Sonstige Experten & Dienstleistungen',
];
// Для строительных экспертов — те же категории раздела Experten, проектные первыми.
const CATS_BAUEXPERT = [
  'Architekten, Ingenieure & Designer', 'Sachverständige & Tenderberatung',
  ...CATS_EXPERT.filter(c => c !== 'Architekten, Ingenieure & Designer' && c !== 'Sachverständige & Tenderberatung'),
];
const CATS_SHOP = [
  'Baustoffe & Rohbau', 'Türen, Fenster & Möbel', 'Bodenbeläge & Fliesen',
  'Elektro & Beleuchtung', 'Innenausbau & Trockenbau', 'Werkzeuge & Maschinen',
  'Farben, Lacke & Putze', 'Sanitär & Heizung', 'Dach & Regenrinnen',
  'Metall & Stahlprodukte', 'Garten- & Außenbereich', 'Arbeitsschutz & Baustellenzubehör',
];

const BRANCHES = {
  build: {
    icon: '🏗', title: 'Строительная фирма / бригада',
    desc: 'Выполняете строительные и ремонтные работы: плитка, малярка, электрика, сантехника, кровля…',
    specChips: ['Fliesenleger', 'Maler', 'Trockenbau', 'Elektrik', 'Sanitär', 'Dachdecker',
      'Bodenleger', 'Innenausbau', 'Sanierung', 'Renovierung', 'GaLaBau', 'Fenster & Türen'],
    categories: CATS_BUILD,
    namePh: 'Например: STM Bau GmbH',
    descPh: 'Делаем ремонт под ключ в Берлине и окрестностях. 8 лет опыта, своя бригада, работаем по договору.',
    serviceSuggest: ['Ванные под ключ', 'Плитка', 'Малярные работы', 'Ламинат и винил', 'Электромонтаж', 'Демонтаж'],
    servicePh: 'Например: ванные под ключ',
    diffPh: 'Фиксируем цену в договоре, убираем за собой, гарантия на работы…',
    certPh: 'Meisterbrief, TÜV, членство в палате…',
    regionsLabel: 'Регионы работы', regionsPh: 'München, Bayern, deutschlandweit…', onlineChip: false,
    photoTitle: 'Логотип и фото работ', photoNote: 'Покажите свои объекты — карточки с фото выглядят надёжнее и получают больше внимания.',
  },
  expert: {
    icon: '⚖️', title: 'Эксперт',
    desc: 'Консультируете и сопровождаете бизнес: Steuerberater, юрист, страхование, финансирование, бухгалтерия…',
    specChips: ['Steuerberater', 'Buchhaltung', 'Rechtsanwalt', 'Versicherung', 'Finanzierung',
      'Unternehmensberatung', 'Marketing', 'Übersetzer'],
    categories: CATS_EXPERT,
    namePh: 'Например: Steuerkanzlei Muster',
    descPh: 'Ведём бухгалтерию и налоги строительных фирм по всей Германии. Говорим по-русски и по-немецки.',
    serviceSuggest: ['Консультации', 'Ведение бухгалтерии', 'Годовые отчёты', 'Договоры', 'Регистрация фирмы'],
    servicePh: 'Например: ведение бухгалтерии',
    diffPh: 'Говорим по-русски и по-немецки, отвечаем в тот же день, знаем специфику строителей…',
    certPh: 'Steuerberaterkammer, допуски, дипломы…',
    regionsLabel: 'Регионы консультаций', regionsPh: 'München, Bayern…', onlineChip: true,
    photoTitle: 'Логотип и фото офиса', photoNote: 'Фото офиса и команды добавляют доверия — клиент видит живых людей.',
  },
  bauexpert: {
    icon: '📐', title: 'Строительный эксперт',
    desc: 'Проектируете и сопровождаете стройку: архитектор, инженер, статик, дизайнер, Gutachter…',
    specChips: ['Architekt', 'Bauingenieur', 'Statiker', 'Innenarchitekt', 'Bauleiter',
      'Sachverständiger (Gutachter)', 'Energieberater', 'Vermessung'],
    categories: CATS_BAUEXPERT,
    namePh: 'Например: Ingenieurbüro Muster',
    descPh: 'Проектируем и согласовываем: архитектура, статика, разрешения. Сопровождаем от идеи до сдачи.',
    serviceSuggest: ['Проектирование', 'Статические расчёты', 'Согласования', 'Авторский надзор', '3D-визуализация', 'Обмеры'],
    servicePh: 'Например: статические расчёты',
    diffPh: 'Сопровождаем согласования до разрешения, считаем быстро, работаем со стройфирмами…',
    certPh: 'Ingenieurkammer / Architektenkammer, допуски, сертификаты…',
    regionsLabel: 'Регионы проектов', regionsPh: 'München, Bayern…', onlineChip: true,
    photoTitle: 'Логотип и фото проектов', photoNote: 'Покажите реализованные проекты — визуализации, чертежи, объекты.',
  },
  shop: {
    icon: '📦', title: 'Магазин / дистрибьютор',
    desc: 'Продаёте материалы, инструмент, оборудование: Baustoffe, двери и окна, электрика…',
    specChips: ['Baustoffe', 'Werkzeuge & Maschinen', 'Türen & Fenster', 'Bodenbeläge & Fliesen',
      'Elektro', 'Farben & Lacke', 'Sanitär & Heizung', 'Metall'],
    categories: CATS_SHOP,
    namePh: 'Например: Baustoffe Muster GmbH',
    descPh: 'Продаём напольные покрытия и двери со склада в Мюнхене. Доставка по Баварии, оптовые цены для фирм.',
    serviceSuggest: ['Продажа', 'Доставка', 'Распил', 'Подбор материалов', 'Оптовые цены', 'Монтаж'],
    servicePh: 'Например: доставка по региону',
    diffPh: 'Склад в наличии, доставка 24–48 часов, скидки для фирм…',
    certPh: 'Официальный дилер, сертификаты качества…',
    regionsLabel: 'Регионы доставки', regionsPh: 'München und Umgebung, Bayern…', onlineChip: false,
    photoTitle: 'Логотип и фото магазина', photoNote: 'Фото товаров, склада и торгового зала — клиент видит, что вы реальные.',
  },
};
const branchOf = (values) => BRANCHES[values.companyType] || BRANCHES.build;

// Экраны анкеты (после интро). Каждый — маленький: короткое «зачем» + 1–3 поля.
const SCREENS = ['type', 'contacts', 'address', 'legal', 'name', 'about',
  'categories', 'services', 'diff', 'media', 'links', 'review'];

// Заголовок и подводка экрана (тексты утверждены владельцем; зависят от ветки).
function screenMeta(id, br) {
  switch (id) {
    case 'type':       return { title: 'Выберите тип вашей фирмы', note: 'От этого зависит раздел платформы, где вас увидят клиенты, и следующие вопросы.' };
    case 'contacts':   return { title: 'Контактные данные', note: 'Показываются в блоке «Kontakt» на карточке — по ним клиенты будут связываться с вами.' };
    case 'address':    return { title: 'Адрес фирмы', note: 'На карточке адрес показывается с картой и кнопкой «маршрут».' };
    case 'legal':      return { title: 'Юридическая информация', note: 'Даёт карточке отметку «Geprüftes Unternehmen» — проверенным фирмам клиенты доверяют больше.' };
    case 'name':       return { title: 'Название и специализация', note: 'Первое, что видит клиент, — заголовок вашей карточки.' };
    case 'about':      return { title: 'Расскажите о фирме', note: 'Из этого мы соберём блок «Firmenprofil» — рассказ о вас на карточке.' };
    case 'categories': return { title: 'Категории услуг', note: 'Категории — это разделы каталога: по ним клиенты находят вас в поиске платформы. Выберите все, которые реализуете.' };
    case 'services':   return { title: 'Ваши услуги', note: 'Категории — разделы каталога, где вас ищут. А услуги — конкретный список того, что вы делаете: он виден на самой карточке.' };
    case 'diff':       return { title: 'Чем вы отличаетесь?', note: 'Попадёт в раздел «Почему мы» — то, что выделит вас среди других фирм.' };
    case 'media':      return { title: br.photoTitle, note: br.photoNote };
    case 'links':      return { title: 'Сайт и соцсети', note: 'Тоже показываются на карточке — чем полнее профиль, тем солиднее выглядит фирма.' };
    case 'review':     return { title: 'Проверьте и подтвердите', note: 'Проверьте все данные — так их увидят клиенты. Если всё верно, подтвердите внизу.' };
    default:           return { title: '', note: '' };
  }
}

const str = (v) => (v == null ? '' : String(v).trim());

// Специализация одной строкой: чипы + свой вариант.
function specSummary(values) {
  const arr = Array.isArray(values.profileTag) ? values.profileTag : [];
  const custom = str(values.profileTag__custom);
  return [...arr, custom].filter(Boolean).join(', ');
}

// Адрес, который показываем на карточке (юридический / фактический — выбор клиента).
function cardAddress(values) {
  if (values.addressSameAsLegal === false && values.addressShow === 'actual') return str(values.addressActual);
  return str(values.addressLegal);
}

// Незаполненные обязательные поля экрана → [{label}]. Опциональные экраны всегда ок.
function screenMissing(id, values) {
  const miss = [];
  const need = (bad, label) => { if (bad) miss.push(label); };
  switch (id) {
    case 'type':
      need(!values.companyType, 'Тип фирмы'); break;
    case 'contacts':
      need(!str(values.phone), 'Телефон');
      need(!str(values.email), 'E-mail');
      need(!str(values.responsiblePerson), 'Контактное лицо'); break;
    case 'address':
      need(!str(values.addressLegal), 'Юридический адрес');
      if (values.addressSameAsLegal === false) {
        need(!str(values.addressActual), 'Фактический адрес');
        need(!values.addressShow, 'Какой адрес показать');
      }
      need(!str(values.city), 'Основной город'); break;
    case 'legal':
      need(!str(values.legalForm), 'Орг-форма'); break;
    case 'name':
      need(!str(values.companyName), 'Название фирмы');
      need((!Array.isArray(values.profileTag) || values.profileTag.length === 0) && !str(values.profileTag__custom), 'Специализация');
      break;
    case 'about':
      need(!str(values.companyDescription), 'Описание фирмы'); break;
    case 'categories':
      need(!Array.isArray(values.categories) || values.categories.length === 0, 'Категории'); break;
    case 'services':
      need(!Array.isArray(values.services) || values.services.length === 0, 'Услуги');
      need(!str(values.workRegions), 'Регионы'); break;
    default: break; // diff / media / links — по желанию; review — свой чекбокс
  }
  return miss;
}
// Все незаполненные обязательные по всей анкете → [{label, screen: idx}]
function allMissing(values) {
  return SCREENS.flatMap((id, idx) =>
    screenMissing(id, values).map(label => ({ label, screen: idx })));
}

// ─── Черновик анкеты: автосохранение в localStorage ─────────────────────────
// TODO(backend): перенести в профиль (черновик переживёт смену устройства).
const DRAFT_KEY = 'fixhub_postpay_reg_draft';
function loadDraft() {
  try { return JSON.parse(localStorage.getItem(DRAFT_KEY)) || {}; } catch { return {}; }
}
// localStorage — мгновенный автосейв (на каждый ввод). Бэкенд-зеркало (переживает
// смену устройства) шлётся отдельно, с дебаунсом — см. эффект автосейва ниже.
function saveDraft(d) {
  try { localStorage.setItem(DRAFT_KEY, JSON.stringify(d)); } catch { /* noop */ }
}
function clearDraft() {
  try { localStorage.removeItem(DRAFT_KEY); } catch { /* noop */ }
}

// ─── Гейтинг слайдов по пакету ──────────────────────────────────────────────
//   start    → success · welcome
//   standard/pro → + анкета карточки (registration)
// (Слайд Chat-Angebot/сметы убран по решению владельца 09.07.)
// Источник — GET /api/subscription {plan}. Пусто/неизвестно → фолбэк 'standard'.
const PLAN_ORDER = ['start', 'standard', 'pro'];
function normalizePlan(raw) {
  const p = String(raw || '').trim().toLowerCase();
  return PLAN_ORDER.includes(p) ? p : '';
}
// Разрешение пакета: dev-override (localStorage) > проп/ответ API > фолбэк 'standard'.
// Dev-override 'fixhub_postpay_plan' (start|standard|pro) даёт владельцу-админу —
// у него в ответе /api/subscription нет поля plan — прокликать все три матрицы в песочнице.
function resolvePlan(planProp) {
  try {
    const ls = normalizePlan(localStorage.getItem('fixhub_postpay_plan'));
    if (ls) return ls;
  } catch { /* noop */ }
  return normalizePlan(planProp) || 'standard';
}


// ─── Данные покупки для success-слайда (из /api/subscription) ────────────────
// Читаемые имена пакетов. TODO(владелец): финальные маркетинговые названия.
const PLAN_NAMES = { start: 'Пакет Start', standard: 'Пакет Standard', pro: 'Пакет Pro' };
function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
}
// raw {plan, plan_period, next_billing, expires} → display {plan, termLabel, nextBilling}.
// Возвращает null, если данных нет (success-слайд тогда возьмёт стаб PURCHASE_INFO).
// Промо-доступ (plan_period='promo', акция KAIZENBAUCLUB): вместо периода/списания —
// фиксированная дата окончания акции (expires; фолбэк — дата первой акции).
function buildPurchase(raw) {
  if (!raw) return null;
  const plan = normalizePlan(raw.plan);
  const name = PLAN_NAMES[plan] || (plan ? `Пакет ${plan}` : '');
  if (raw.plan_period === 'promo') {
    return {
      plan: name || 'Пакет Start', termLabel: 'Бесплатно по промокоду', nextBilling: '',
      promo: true, accessUntil: fmtDate(raw.expires) || '15.08.2026',
    };
  }
  const termLabel = raw.plan_period === 'month' ? 'Помесячно'
    : raw.plan_period === 'year' ? 'На 12 месяцев' : '';
  const nextBilling = fmtDate(raw.next_billing);
  if (!name && !termLabel && !nextBilling) return null;
  return { plan: name, termLabel, nextBilling };
}

// ─── Обёртка: гейт «ждём подтверждения оплаты» → карусель ────────────────────
// Stripe-вебхук (checkout.session.completed) прилетает АСИНХРОННО. Юзер возвращается
// в аппку сразу после оплаты → subscription_active/plan могут быть ещё не проставлены.
// Поэтому при свежей оплате (justPaid) сначала показываем PaymentPendingSlide (поллит
// GET /api/subscription до active===true), и только потом монтируем карусель с уже
// известным пакетом. Перезаход (не justPaid) → карусель сразу, пакет из пропа/override.
export default function PostPaymentDev(props) {
  const { justPaid = true, plan: planProp } = props;

  // Dev-override пакета известен синхронно → пропускаем ожидание (владелец-админ).
  const devPlan = (() => {
    try { return normalizePlan(localStorage.getItem('fixhub_postpay_plan')); } catch { return ''; }
  })();

  const [ready, setReady] = useState(() => !justPaid || !!devPlan);
  const [resolvedPlan, setResolvedPlan] = useState(() => devPlan || normalizePlan(planProp));
  const [resolvedData, setResolvedData] = useState(null); // ответ /api/subscription (plan/период/дата)

  const handleResolved = useCallback((planCode, data) => {
    setResolvedPlan(planCode || '');
    if (data) setResolvedData(data);
    setReady(true);
  }, []);

  if (!ready) {
    return <PaymentPendingSlide fallbackPlan={resolvedPlan} onResolved={handleResolved} />;
  }
  // Данные покупки для success-слайда: из подтверждённого ответа поллинга, иначе из пропа
  // App.js (checkSubscription), иначе success-слайд возьмёт стаб PURCHASE_INFO.
  const purchaseDisplay = buildPurchase(resolvedData) || buildPurchase(props.purchase);
  return <PostPaymentCarousel {...props} plan={resolvedPlan || planProp} purchase={purchaseDisplay} />;
}

// Поллинг подтверждения оплаты. Разрешаем, как только active===true — plan читаем из
// того же ответа (webhook пишет active и plan в одном обработчике). Таймаут → фолбэк:
// доступ уже открыт вебхуком в фоне, не держим клиента; пакет падает в 'standard' в карусели.
const PP_POLL_INTERVAL_MS = 2500;
const PP_POLL_TIMEOUT_MS = 60000;

function PaymentPendingSlide({ fallbackPlan, onResolved }) {
  const [timedOut, setTimedOut] = useState(false);
  const doneRef = useRef(false);

  const finish = useCallback((planCode, data) => {
    if (doneRef.current) return;
    doneRef.current = true;
    onResolved(planCode, data);
  }, [onResolved]);

  useEffect(() => {
    let cancelled = false;
    const started = Date.now();
    let timer;
    const schedule = () => {
      if (cancelled || doneRef.current) return;
      if (Date.now() - started >= PP_POLL_TIMEOUT_MS) { setTimedOut(true); return; }
      timer = setTimeout(tick, PP_POLL_INTERVAL_MS);
    };
    const tick = () => {
      getSubscription()
        .then((data) => {
          if (cancelled || doneRef.current) return;
          if (data && data.active === true) { finish(normalizePlan(data.plan), data); return; }
          schedule();
        })
        .catch(() => { schedule(); });
    };
    tick();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [finish]);

  return (
    <div className="onb-root pp-root pp-pending" style={{
      position: 'fixed', left: 0, right: 0, top: 0, bottom: 0, zIndex: 10000,
      background: 'var(--bg-primary)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '32px 28px', textAlign: 'center',
      paddingTop: 'var(--tg-safe-top, 0px)', paddingBottom: 'var(--tg-safe-bottom, 0px)',
    }}>
      {!timedOut ? (
        <>
          <div className="pp-pending-spin" />
          <div style={{ marginTop: 26, fontSize: 19, fontWeight: 700, color: 'var(--text-primary)' }}>
            Проверяем оплату…
          </div>
          <div style={{ marginTop: 10, fontSize: 14, lineHeight: 1.5, color: 'var(--text-tertiary)', maxWidth: 300 }}>
            Подтверждаем платёж — это займёт пару секунд. Не закрывайте приложение.
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 40 }}>⏳</div>
          <div style={{ marginTop: 16, fontSize: 19, fontWeight: 700, color: 'var(--text-primary)' }}>
            Оплата обрабатывается
          </div>
          <div style={{ marginTop: 10, fontSize: 14, lineHeight: 1.5, color: 'var(--text-tertiary)', maxWidth: 320 }}>
            Иногда подтверждение занимает пару минут. Доступ уже открыт — можно продолжить,
            а детали покупки подтянутся автоматически.
          </div>
          <button onClick={() => finish(fallbackPlan)} style={{
            marginTop: 24, padding: '13px 26px', borderRadius: 12, border: 'none',
            background: 'var(--accent)', color: '#111', fontSize: 15, fontWeight: 700, cursor: 'pointer',
          }}>Продолжить</button>
        </>
      )}
      <style>{`
        @keyframes pp-pending-rotate { to { transform: rotate(360deg); } }
        .pp-pending-spin {
          width: 46px; height: 46px; border-radius: 50%;
          border: 3.5px solid var(--bg-elevated); border-top-color: var(--accent);
          animation: pp-pending-rotate .9s linear infinite;
        }
        .pp-pending button:active { transform: scale(0.97); transition: transform .08s ease; }
        @media (prefers-reduced-motion: reduce) { .pp-pending-spin { animation: none; } }
      `}</style>
    </div>
  );
}

// ─── Каркас: карусель послеоплатных слайдов ─────────────────────────────────
// Монтируется обёрткой PostPaymentDev уже с известным пакетом (plan) — после того,
// как обёртка дождалась подтверждения оплаты (см. PaymentPendingSlide).
function PostPaymentCarousel({ onComplete, purchase, justPaid = true, plan, hasEstimatesAddon, forceRegistration = false, grant = false }) {
  // Список слайдов фиксируем на маунт (чтобы отметка «больше не показывать» на текущем
  // слайде не убирала его из-под пользователя — она сработает при следующем входе).
  //  • слайд 1 (success) — разовый, только сразу после оплаты (justPaid) — в гейт не входит;
  //  • анкета (registration) — пакеты standard/pro;
  //  • повторяющиеся слайды показываются, если не отмечено «больше не показывать».
  const [slides] = useState(() => {
    // Ручная выдача из админки (grant): пакет берём как есть, БЕЗ фолбэка 'standard' —
    // бесплатнику/совладельцу без пакета анкету карточки не показываем, только инструкцию.
    const planCode = grant ? normalizePlan(plan) : resolvePlan(plan);
    // Промо (KAIZENBAUCLUB): только подтверждение + инструкция, без анкеты — независимо от пакета.
    const promoMode = !!(purchase && purchase.promo);
    const showRegistration = !promoMode && (planCode === 'standard' || planCode === 'pro');
    // forceRegistration (баннер на главной / деп-линк card_form): человек пришёл
    // заполнять анкету — открываем её сразу, без welcome-видео с галочкой.
    if (forceRegistration && showRegistration) return [{ id: 'registration', content: 'registration' }];
    const list = [];
    if (justPaid) list.push({ id: 'success', content: 'success' });
    // Промо: инструкция — суть подтверждения, показываем ВСЕГДА (иначе давняя галочка
    // «больше не показывать» от прежней подписки молча съедает слайд с видео).
    // Ручная выдача (grant): то же — владелец открыл доступ → инструкция показывается всегда.
    if (promoMode || grant || !isSlideHidden('welcome')) list.push({ id: 'welcome', content: 'welcome' });
    // forceRegistration (открытие с баннера/деп-линка card_form): галочка «больше не
    // показывать» не прячет анкету — иначе клик по баннеру молча закрывался бы.
    if (showRegistration && (forceRegistration || !isSlideHidden('registration'))) list.push({ id: 'registration', content: 'registration' });
    return list;
  });

  const [current, setCurrent] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const containerRef = useRef(null);

  // Переход к следующему слайду; на последнем — вход в приложение.
  const goNext = useCallback(() => {
    if (current < slides.length - 1) {
      setAnimKey((k) => k + 1);
      setCurrent((c) => c + 1);
    } else {
      onComplete?.();
    }
  }, [current, slides.length, onComplete]);

  const goPrev = useCallback(() => {
    if (current > 0) { setAnimKey((k) => k + 1); setCurrent((c) => c - 1); }
  }, [current]);

  // Кнопка «Назад» умеет делегировать активному слайду (напр. пошаговая анкета сама
  // отматывает секции), и только если слайд не перехватил — уходим на предыдущий слайд.
  const backFnRef = useRef(null);
  const registerBack = useCallback((fn) => { backFnRef.current = fn; }, []);
  const handleBack = useCallback(() => {
    if (backFnRef.current && backFnRef.current()) return;
    goPrev();
  }, [goPrev]);

  // Блокируем прокрутку body, пока показаны слайды (как в OnboardingDev).
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Показывать нечего (не justPaid и все повторяющиеся скрыты) → сразу входим в приложение.
  useEffect(() => {
    if (slides.length === 0) onComplete?.();
  }, [slides.length, onComplete]);

  const translateX = -(current * 100);

  return (
    <div className="onb-root pp-root" style={{
      position: 'fixed', left: 0, right: 0, top: 0, zIndex: 10000,
      height: '100%',
      background: 'var(--bg-primary)',
      display: 'flex', flexDirection: 'column',
      paddingTop: 'var(--tg-safe-top, 0px)',
      paddingBottom: 'var(--tg-safe-bottom, 0px)',
    }}>
      {/* Прогресс-бар — по плашке на слайд (как в воронке) */}
      <div style={{ padding: '12px 16px 0', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 5 }}>
          {slides.map((_, i) => (
            <div key={i} style={{
              flex: 1, height: 3, borderRadius: 2,
              background: 'var(--bg-elevated)', overflow: 'hidden',
            }}>
              <div style={{
                height: '100%', borderRadius: 2, background: 'var(--accent)',
                width: i < current ? '100%' : i === current ? '45%' : '0%',
                transition: 'width 0.4s ease',
              }} />
            </div>
          ))}
        </div>
      </div>

      {/* Слайды */}
      <div ref={containerRef} style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        <div style={{
          display: 'flex', height: '100%',
          transform: `translateX(${translateX}%)`,
          transition: 'transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          willChange: 'transform',
        }}>
          {slides.map((slide, i) => (
            // ВАЖНО: НЕ justifyContent:center на скролл-контейнере — верх длинного
            // контента становится недостижим (не проскроллить). Центрирование коротких
            // слайдов делает margin:auto у внутренней обёртки — она же даёт скролл длинным.
            <div key={slide.id} style={{
              minWidth: '100%', height: '100%',
              overflowY: 'auto', WebkitOverflowScrolling: 'touch',
              scrollPaddingBottom: 40, boxSizing: 'border-box',
            }}>
              <div style={{
                minHeight: '100%', width: '100%', boxSizing: 'border-box',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                padding: '20px 24px',
              }}>
              {slide.content === 'success' && (
                <PaymentSuccessSlide
                  animKey={animKey}
                  purchase={purchase || PURCHASE_INFO}
                  onNext={goNext}
                />
              )}
              {slide.content === 'welcome' && (
                <WelcomeInsideSlide
                  animKey={animKey}
                  slideId={slide.id}
                  onNext={goNext}
                />
              )}
              {slide.content === 'registration' && (
                <RegistrationSlide
                  animKey={animKey}
                  slideId={slide.id}
                  active={i === current}
                  registerBack={registerBack}
                  onNext={goNext}
                  forceRegistration={forceRegistration}
                />
              )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Нижняя панель «Назад» — как в дооплатной воронке (не налезает на контент) */}
      <div style={{
        padding: '12px 24px 20px', flexShrink: 0, minHeight: 52,
        display: 'flex', justifyContent: 'center', alignItems: 'center',
      }}>
        {current > 0 && (
          <span onClick={handleBack} style={{
            cursor: 'pointer', padding: '8px 16px',
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 14, fontWeight: 600,
          }}>
            <span style={{ color: 'var(--accent)', fontSize: 20, fontWeight: 700, lineHeight: 1 }}>←</span>
            <span style={{ color: 'var(--text-tertiary)' }}>Назад</span>
          </span>
        )}
      </div>

      <style>{`
        .pp-root button { -webkit-tap-highlight-color: transparent; }
        .pp-root button:not(:disabled):active { transform: scale(0.97); transition: transform .08s ease; }
        .pp-root .pp-input { transition: border-color .18s ease; }
        .pp-root .pp-input:focus { border-color: var(--accent); }
        .pp-root select.pp-input { -webkit-appearance: none; appearance: none; }
        .pp-root .pp-input::placeholder { color: var(--text-tertiary); }
        @keyframes onb-fadeUp {
          from { opacity: 0; transform: translateY(16px); filter: blur(4px); }
          to { opacity: 1; transform: translateY(0); filter: blur(0); }
        }
        @keyframes onb-scaleIn {
          from { opacity: 0; transform: scale(0.94); filter: blur(4px); }
          to { opacity: 1; transform: scale(1); filter: blur(0); }
        }
        /* Пульсирующее свечение золотой галочки */
        @keyframes pp-glow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255,212,59,0.34), 0 0 42px 10px rgba(255,212,59,0.22); }
          50%      { box-shadow: 0 0 0 14px rgba(255,212,59,0), 0 0 60px 18px rgba(255,212,59,0.30); }
        }
        .pp-anim-1 { animation: onb-scaleIn 0.6s cubic-bezier(0.22,0.68,0,1) both; animation-delay: 0.02s; }
        .pp-anim-2 { animation: onb-fadeUp 0.6s cubic-bezier(0.22,0.68,0,1) both; animation-delay: 0.14s; }
        .pp-anim-3 { animation: onb-fadeUp 0.6s cubic-bezier(0.22,0.68,0,1) both; animation-delay: 0.24s; }
        .pp-anim-4 { animation: onb-fadeUp 0.6s cubic-bezier(0.22,0.68,0,1) both; animation-delay: 0.34s; }
        .pp-check-glow { animation: pp-glow 2.4s ease-in-out infinite; animation-delay: 0.7s; }
        /* Чёрно-жёлтое конфети-взрыв за галочкой (разово, при показе слайда) */
        .pp-confetti-wrap {
          position: absolute; left: 50%; top: 50%; width: 0; height: 0;
          transform: translate(-50%, -50%); pointer-events: none; z-index: 0;
        }
        .pp-confetti-pc {
          position: absolute; left: 0; top: 0; opacity: 0;
          will-change: transform, opacity;
          animation: pp-confetti 1.15s cubic-bezier(.2,.7,.3,1) forwards;
          animation-delay: 0.25s;
        }
        @keyframes pp-confetti {
          0%   { opacity: 0; transform: translate(0,0) rotate(0deg) scale(.4); }
          12%  { opacity: 1; }
          100% { opacity: 0; transform: translate(var(--tx), var(--ty)) rotate(var(--rot)) scale(1); }
        }
        /* Крутилка загрузки фото (превью в анкете) */
        @keyframes pp-rotate { to { transform: rotate(360deg); } }
        .pp-upspin {
          width: 20px; height: 20px; border-radius: 50%;
          border: 2.5px solid rgba(255,255,255,0.3); border-top-color: #fff;
          animation: pp-rotate .8s linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .pp-anim-1, .pp-anim-2, .pp-anim-3, .pp-anim-4 { animation: none; }
          .pp-check-glow { animation: none; }
          .pp-confetti-pc { display: none; }
          .pp-upspin { animation: none; }
        }
      `}</style>
    </div>
  );
}

// Пиши-разово-считаем куски конфети: чёрно-жёлтый радиальный «взрыв» из-за галочки.
// Детерминировано по индексу (без Math.random в рендере) — angle по кругу + разброс.
function buildConfetti() {
  const N = 64;
  return Array.from({ length: N }, (_, i) => {
    const angle = (i / N) * Math.PI * 2 + (i % 2 ? 0.18 : -0.12);
    const dist = 150 + ((i * 17) % 150);               // 150..300 px разлёт (шире)
    const tx = Math.cos(angle) * dist;
    const ty = Math.sin(angle) * dist + 70;            // +гравитация вниз
    const rot = (i % 2 ? 1 : -1) * (200 + ((i * 57) % 360));
    const dark = i % 2 === 1;                           // чередуем жёлтый / чёрный
    return {
      tx, ty, rot, dark,
      w: 8 + (i % 3) * 3,                               // крупнее: 8..14
      h: 12 + (i % 2) * 8,                              // 12..20
      round: i % 4 === 0,
      delay: 0.2 + (i % 7) * 0.028,
    };
  });
}

// ─── Слайд 1: «Оплата прошла успешно» (разовый, сразу после оплаты) ─────────
function PaymentSuccessSlide({ animKey, purchase, onNext }) {
  const { plan, termLabel, nextBilling, promo, accessUntil } = purchase || {};
  const confetti = useMemo(() => buildConfetti(), []);

  // Параллельно с показом — бот шлёт подтверждение в Telegram (backend-хук-стаб).
  // Один раз на маунт слайда. Промо: DM шлёт сам бэкенд при redeem — здесь не дублируем.
  const notified = useRef(false);
  useEffect(() => {
    if (notified.current) return;
    notified.current = true;
    if (!promo) notifyPayment({ plan });
  }, [plan, promo]);

  return (
    <div style={{ textAlign: 'center', width: '100%', maxWidth: 400 }}>
      {/* Золотая галочка со свечением + чёрно-жёлтое конфети-взрыв за ней */}
      <div key={`s1-${animKey}`} className="pp-anim-1" style={{
        display: 'flex', justifyContent: 'center', marginBottom: 30,
      }}>
        <div style={{ position: 'relative', width: 108, height: 108 }}>
          {/* Конфети (позади галочки) */}
          <div className="pp-confetti-wrap" aria-hidden="true">
            {confetti.map((c, i) => (
              <span key={i} className="pp-confetti-pc" style={{
                '--tx': `${c.tx}px`, '--ty': `${c.ty}px`, '--rot': `${c.rot}deg`,
                width: c.w, height: c.h,
                borderRadius: c.round ? '50%' : 2,
                background: c.dark ? '#141414' : 'var(--accent)',
                boxShadow: c.dark ? '0 0 0 1px rgba(255,212,59,0.35)' : 'none',
                animationDelay: `${c.delay}s`,
              }} />
            ))}
          </div>
          <span className="pp-check-glow" style={{
            position: 'relative', zIndex: 1,
            width: 108, height: 108, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'radial-gradient(circle at 50% 38%, #FFE27A, var(--accent) 72%)',
          }}>
            {/* Жирная тёмная галочка (inline-SVG — толщину/цвет иконка ./icons не пробрасывает) */}
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none"
              stroke="#0D0D0D" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
              style={{ display: 'block' }}>
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </span>
        </div>
      </div>

      <h1 key={`s2-${animKey}`} className="pp-anim-2" style={{
        fontSize: 24, fontWeight: 800, marginBottom: 12,
        color: 'var(--text-primary)', lineHeight: 1.3, letterSpacing: '-0.4px',
      }}>
        {promo ? 'Промокод активирован 🎉' : 'Оплата прошла успешно 🎉'}
      </h1>

      <p key={`s3-${animKey}`} className="pp-anim-3" style={{
        fontSize: 15, color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 22px',
      }}>
        {promo
          ? `Вы получили бесплатный доступ к приложению до ${accessUntil}. Дальше — короткая инструкция, с чего начать.`
          : 'Доступ открыт. Сейчас покажем, с чего начать.'}
      </p>

      {/* Карточка покупки: пакет + срок / дата следующего списания */}
      <div key={`s4-${animKey}`} className="pp-anim-3" style={{
        marginBottom: 30, padding: '16px 18px',
        background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16,
      }}>
        <div style={{
          fontSize: 11, fontWeight: 700, letterSpacing: '0.7px', textTransform: 'uppercase',
          color: 'var(--text-tertiary)', marginBottom: 7,
        }}>
          {promo ? 'Ваш доступ' : 'Ваш пакет'}
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          fontSize: 17, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1.3,
        }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />
          {plan}
        </div>
        {(termLabel || nextBilling || (promo && accessUntil)) && (
          <div style={{
            marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)',
            fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5,
          }}>
            {termLabel && <div>{termLabel}</div>}
            {promo && accessUntil && (
              <div style={{ color: 'var(--text-tertiary)', fontSize: 12.5, marginTop: 2 }}>
                Доступ до {accessUntil}
              </div>
            )}
            {nextBilling && (
              <div style={{ color: 'var(--text-tertiary)', fontSize: 12.5, marginTop: 2 }}>
                Следующее списание — {nextBilling}
              </div>
            )}
          </div>
        )}
      </div>

      <div key={`s5-${animKey}`} className="pp-anim-4">
        <button onClick={onNext} style={{
          width: '100%', padding: '16px 0',
          background: 'var(--accent)', color: '#0D0D0D',
          border: 'none', borderRadius: 14, fontSize: 16, fontWeight: 700, cursor: 'pointer',
          boxShadow: '0 8px 24px rgba(255,204,0,0.28)',
        }}>
          Продолжить →
        </button>
      </div>
    </div>
  );
}

// ─── Общие мелочи ───────────────────────────────────────────────────────────
// Тёмная галочка (иконка ./icons не пробрасывает цвет/толщину — используем inline-SVG).
function InlineCheck({ size = 15, color = '#0D0D0D', width = 3 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={width} strokeLinecap="round" strokeLinejoin="round"
      style={{ display: 'block' }}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

// Гейт-чекбокс-карточка (зеркало GateCheckbox из OnboardingDev).
function GateCheckbox({ checked, onToggle, label }) {
  return (
    <div onClick={onToggle} style={{
      display: 'flex', alignItems: 'center', gap: 11, cursor: 'pointer',
      padding: '14px 14px', textAlign: 'left',
      background: 'var(--bg-card)', border: `1.5px solid ${checked ? 'var(--accent)' : 'var(--border)'}`,
      borderRadius: 14, marginBottom: 14, transition: 'border-color 0.2s',
    }}>
      <span style={{
        width: 24, height: 24, borderRadius: 7, flexShrink: 0,
        border: `2px solid ${checked ? 'var(--accent)' : 'var(--border)'}`,
        background: checked ? 'var(--accent)' : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {checked && <InlineCheck size={15} />}
      </span>
      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.35 }}>{label}</span>
    </div>
  );
}

// Вторичный чекбокс «Больше не показывать» — приглушённый, по центру, без коробки.
function DontShowRow({ checked, onToggle }) {
  return (
    <div onClick={onToggle} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
      cursor: 'pointer', padding: '14px 8px 2px',
    }}>
      <span style={{
        width: 18, height: 18, borderRadius: 5, flexShrink: 0,
        border: `1.5px solid ${checked ? 'var(--accent)' : 'var(--border)'}`,
        background: checked ? 'var(--accent)' : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {checked && <InlineCheck size={12} width={3.2} />}
      </span>
      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-tertiary)' }}>Больше не показывать</span>
    </div>
  );
}

// ─── Слайд 2: «Добро пожаловать внутрь» + инструкция (повторяющийся) ─────────
function WelcomeInsideSlide({ animKey, slideId, onNext }) {
  const [agreed, setAgreed] = useState(false);
  const [dontShow, setDontShow] = useState(() => isSlideHidden(slideId));

  const toggleDontShow = () => setDontShow((v) => {
    const nv = !v;
    setSlideHidden(slideId, nv); // сработает при следующем входе
    return nv;
  });

  return (
    <div style={{ width: '100%', maxWidth: 400 }}>
      <h2 key={`w1-${animKey}`} className="pp-anim-1" style={{
        fontSize: 22, fontWeight: 800, marginBottom: 8, textAlign: 'center',
        color: 'var(--text-primary)', lineHeight: 1.3, letterSpacing: '-0.3px',
      }}>
        Теперь — добро пожаловать внутрь
      </h2>

      <p key={`w2-${animKey}`} className="pp-anim-2" style={{
        fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 16px', textAlign: 'center',
      }}>
        Короткое видео покажет, как всё устроено и с чего начать — посмотрите, чтобы уверенно пользоваться приложением.
      </p>

      {/* Встроенный YouTube-плеер в золотой рамке (как на презентации воронки) */}
      <div key={`w3-${animKey}`} className="pp-anim-3" style={{
        padding: 3, borderRadius: 20,
        background: 'linear-gradient(135deg, var(--accent), #E6B800, var(--accent))',
        boxShadow: '0 0 30px rgba(255, 204, 0, 0.15)', marginBottom: 8,
      }}>
        <div style={{
          width: '100%', aspectRatio: '16/9', borderRadius: 17, overflow: 'hidden',
          background: 'var(--bg-card)', position: 'relative',
        }}>
          <iframe
            src={INTRO_VIDEO_URL}
            title="Fix-Hub — как пользоваться приложением"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
          />
        </div>
      </div>
      <p onClick={() => openExternal(INTRO_VIDEO_WATCH_URL)} style={{
        fontSize: 12.5, color: 'var(--text-tertiary)', textAlign: 'center',
        marginBottom: 18, cursor: 'pointer', textDecoration: 'underline',
      }}>
        Открыть видео на YouTube
      </p>

      <GateCheckbox checked={agreed} onToggle={() => setAgreed((a) => !a)}
        label="Ознакомился с инструкцией" />

      <button onClick={() => agreed && onNext()} disabled={!agreed} style={{
        width: '100%', padding: '16px 0',
        background: agreed ? 'var(--accent)' : 'var(--bg-elevated)',
        color: agreed ? '#0D0D0D' : 'var(--text-tertiary)',
        border: 'none', borderRadius: 14, fontSize: 16, fontWeight: 700,
        cursor: agreed ? 'pointer' : 'default', transition: 'all 0.2s',
        boxShadow: agreed ? '0 6px 20px rgba(255,204,0,0.28)' : 'none',
      }}>
        Далее →
      </button>

      <DontShowRow checked={dontShow} onToggle={toggleDontShow} />
    </div>
  );
}

// ─── Слайд 3: анкета fix-hub.de / FIX-CLUB (повторяющийся) ──────────────────
// Плавно поднимает поле над клавиатурой (умеренно, по центру видимой области).
function liftField(e) {
  const el = e.target;
  setTimeout(() => { try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch { /* noop */ } }, 350);
}

const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '12px 14px',
  background: 'var(--bg-elevated)', border: '1px solid var(--border)',
  borderRadius: 12, color: 'var(--text-primary)', fontSize: 16, outline: 'none',
  fontFamily: 'inherit',
};

// ─── Мелкие контролы анкеты ──────────────────────────────────────────────────
// Обёртка поля: подпись + контрол + подсказка.
function Field({ label, required, hint, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      {label && (
        <label style={{
          display: 'block', fontSize: 14.5, fontWeight: 600, color: 'var(--text-secondary)',
          marginBottom: 7, lineHeight: 1.4,
        }}>
          {label}{required && <span style={{ color: 'var(--accent)' }}> *</span>}
        </label>
      )}
      {children}
      {hint && (
        <p style={{ fontSize: 12.5, color: 'var(--text-tertiary)', margin: '5px 0 0', lineHeight: 1.4 }}>{hint}</p>
      )}
    </div>
  );
}

function TextInput({ value, onChange, type = 'text', placeholder = '', rows }) {
  if (rows) {
    return (
      <textarea className="pp-input" rows={rows} value={value || ''} placeholder={placeholder}
        onFocus={liftField} onChange={(e) => onChange(e.target.value)}
        style={{ ...inputStyle, resize: 'vertical', minHeight: 76 }} />
    );
  }
  return (
    <input className="pp-input" type={type} value={value || ''} placeholder={placeholder}
      onFocus={liftField} onChange={(e) => onChange(e.target.value)} style={inputStyle} />
  );
}

function SelectInput({ value, onChange, options, placeholderOption = 'Выберите…' }) {
  return (
    <select className="pp-input" value={value || ''} onChange={(e) => onChange(e.target.value)}
      style={{ ...inputStyle, color: value ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
      <option value="">{placeholderOption}</option>
      {options.map((o) => <option key={o} value={o} style={{ color: '#000' }}>{o}</option>)}
    </select>
  );
}

// Радио-строка (выбор адреса для карточки и т.п.)
function RadioRow({ checked, label, onPick }) {
  return (
    <div onClick={onPick} style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '11px 13px',
      borderRadius: 11, cursor: 'pointer', background: 'var(--bg-elevated)',
      border: `1.5px solid ${checked ? 'var(--accent)' : 'var(--border)'}`, marginBottom: 8,
    }}>
      <span style={{
        width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
        border: `2px solid ${checked ? 'var(--accent)' : 'var(--border)'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {checked && <span style={{ width: 9, height: 9, borderRadius: '50%', background: 'var(--accent)' }} />}
      </span>
      <span style={{ fontSize: 14.5, color: 'var(--text-primary)', lineHeight: 1.35 }}>{label}</span>
    </div>
  );
}

// Чипы-мультивыбор (специализация, категории) — без лимита.
function ChipsPicker({ options, sel, onChange }) {
  const list = Array.isArray(sel) ? sel : [];
  const toggle = (o) => onChange(list.includes(o) ? list.filter((x) => x !== o) : [...list, o]);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
      {options.map((o) => {
        const on = list.includes(o);
        return (
          <button key={o} type="button" onClick={() => toggle(o)} style={{
            padding: '8px 13px', borderRadius: 999, fontSize: 13.5, fontWeight: 600,
            cursor: 'pointer', lineHeight: 1.25, textAlign: 'left',
            background: on ? 'var(--accent)' : 'var(--bg-elevated)',
            color: on ? '#0D0D0D' : 'var(--text-secondary)',
            border: `1.5px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
            transition: 'all .15s',
          }}>
            {on ? '✓ ' : ''}{o}
          </button>
        );
      })}
    </div>
  );
}

// ─── SVG-иконки категорий (дополняют ./icons — стиль тот же: stroke 1.8) ─────
const CSvg = ({ size = 22, children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"
    style={{ display: 'block', flexShrink: 0 }}>{children}</svg>
);
const ILayers = ({ size }) => (<CSvg size={size}><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></CSvg>);
const IPaintbrush = ({ size }) => (<CSvg size={size}><path d="M14 19.9V16h3a2 2 0 002-2v-2H5v2c0 1.1.9 2 2 2h3v3.9a2 2 0 104 0z" /><path d="M6 12V2h12v10" /><path d="M9 5h6" /></CSvg>);
const IDoor = ({ size }) => (<CSvg size={size}><path d="M13 4h3a2 2 0 012 2v14" /><path d="M2 20h20" /><path d="M13 4.562v16.157a1 1 0 01-1.242.97L6 20V5.562a2 2 0 011.515-1.94l4-1A2 2 0 0113 4.562z" /><path d="M10 12v.01" /></CSvg>);
const IDroplets = ({ size }) => (<CSvg size={size}><path d="M7 16.3c2.2 0 4-1.83 4-4.05 0-1.16-.57-2.26-1.71-3.19S7.29 6.75 7 5.3c-.29 1.45-1.14 2.84-2.29 3.76S3 11.1 3 12.25c0 2.22 1.8 4.05 4 4.05z" /><path d="M12.56 6.6A11 11 0 0014 3.02c.5 2.5 2 4.9 4 6.5s3 3.5 3 5.5a7 7 0 01-11.91 4.97" /></CSvg>);
const IZap = ({ size }) => (<CSvg size={size}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></CSvg>);
const ILeaf = ({ size }) => (<CSvg size={size}><path d="M11 20A7 7 0 019.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z" /><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" /></CSvg>);
const ITruck = ({ size }) => (<CSvg size={size}><rect x="1" y="3" width="15" height="13" rx="1" /><path d="M16 8h4l3 3v5h-7V8z" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></CSvg>);
const ITrash = ({ size }) => (<CSvg size={size}><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></CSvg>);
const ICog = ({ size }) => (<CSvg size={size}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" /></CSvg>);
const IKey = ({ size }) => (<CSvg size={size}><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" /></CSvg>);
const ISparkles = ({ size }) => (<CSvg size={size}><path d="M12 3l1.9 5.8a2 2 0 001.3 1.3L21 12l-5.8 1.9a2 2 0 00-1.3 1.3L12 21l-1.9-5.8a2 2 0 00-1.3-1.3L3 12l5.8-1.9a2 2 0 001.3-1.3L12 3z" /></CSvg>);
const IAlert = ({ size }) => (<CSvg size={size}><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></CSvg>);
const IRuler = ({ size }) => (<CSvg size={size}><path d="M21.3 8.7l-6-6a1 1 0 00-1.4 0L2.7 13.9a1 1 0 000 1.4l6 6a1 1 0 001.4 0L21.3 10.1a1 1 0 000-1.4z" /><path d="M7.5 10.5l2 2" /><path d="M10.5 7.5l2 2" /><path d="M13.5 4.5l2 2" /><path d="M4.5 13.5l2 2" /></CSvg>);
const ISearch = ({ size }) => (<CSvg size={size}><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></CSvg>);
const IArmchair = ({ size }) => (<CSvg size={size}><path d="M19 9V6a2 2 0 00-2-2H7a2 2 0 00-2 2v3" /><path d="M3 16a2 2 0 002 2h14a2 2 0 002-2v-5a2 2 0 00-4 0v2H7v-2a2 2 0 00-4 0v5z" /><path d="M5 18v2" /><path d="M19 18v2" /></CSvg>);
const ILamp = ({ size }) => (<CSvg size={size}><path d="M9 18h6" /><path d="M10 22h4" /><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0018 8 6 6 0 006 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 018.91 14" /></CSvg>);
const IShieldHat = ({ size }) => (<CSvg size={size}><path d="M2 18h20" /><path d="M4 18v-3a8 8 0 0116 0v3" /><path d="M10 18V6a2 2 0 114 0v12" /></CSvg>);

// Иконка категории каталога (по названию; нет в мапе → молоток).
const CAT_ICONS = {
  // Строители (fix-hub.de)
  'Rohbau und Innenausbau': Building, 'Bodenarbeiten und Verlegung': ILayers,
  'Fassaden- und Malerarbeiten': IPaintbrush, 'Putz- und Estricharbeiten': Wrench,
  'Fenster- und Türenmontage': IDoor, 'Bauplanung und Projektbetreuung': Clipboard,
  'Bad- und Heizungsinstallation': IDroplets, 'Möbelbau und Montage': IArmchair,
  'Elektrotechnik und Gebäudeverkabelung': IZap, 'Dacharbeiten und Abdichtungssysteme': Home,
  'Garten- und Außenanlagen': ILeaf, 'Umzugshilfe & Möbellogistik': ITruck,
  'Entrümpelung (Privat & Gewerbe)': ITrash, 'Metallbau und Konstruktionstechnik': ICog,
  'Schlüssel- und Hausmeisterdienst': IKey, 'Reinigungsservice': ISparkles,
  'Notfall-Reparaturen 24/7': IAlert,
  // Эксперты (experten.fix-hub.de)
  'Steuerberatung & Buchhaltung': Receipt, 'Rechtsanwälte & Anwälte': Scale,
  'Versicherungsberatung (Gesundheit/Geschäft)': Shield, 'Finanzierung & Kreditberatung': Euro,
  'Gründungsberatung & Personalberatung': TrendingUp, 'Marketing & IT-Beratung': MessageCircle,
  'Immobilienmakler & Notare': Home, 'Dolmetscher & Übersetzer': MessageCircle,
  'Architekten, Ingenieure & Designer': IRuler, 'Sachverständige & Tenderberatung': ISearch,
  'Transport & Logistikdienste': ITruck, 'Sonstige Experten & Dienstleistungen': Star,
  // Магазины (shop.fix-hub.de)
  'Baustoffe & Rohbau': Building, 'Türen, Fenster & Möbel': IDoor,
  'Bodenbeläge & Fliesen': ILayers, 'Elektro & Beleuchtung': ILamp,
  'Innenausbau & Trockenbau': Hammer, 'Werkzeuge & Maschinen': Wrench,
  'Farben, Lacke & Putze': IPaintbrush, 'Sanitär & Heizung': IDroplets,
  'Dach & Regenrinnen': Home, 'Metall & Stahlprodukte': ICog,
  'Garten- & Außenbereich': ILeaf, 'Arbeitsschutz & Baustellenzubehör': IShieldHat,
};

// Выбор категорий: плитки 2 в ряд (как «Преимущества» в воронке) — SVG-иконка
// в жёлтом бейдже сверху, название снизу, чек-кружок в углу + счётчик выбранного.
function CategoryPicker({ options, sel, onChange }) {
  const list = Array.isArray(sel) ? sel : [];
  const toggle = (o) => onChange(list.includes(o) ? list.filter((x) => x !== o) : [...list, o]);
  return (
    <div>
      <div style={{
        fontSize: 12.5, fontWeight: 700, marginBottom: 10,
        color: list.length ? 'var(--accent)' : 'var(--text-tertiary)',
      }}>
        {list.length ? `✓ Выбрано: ${list.length}` : 'Нажмите на подходящие категории'}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {options.map((o) => {
          const on = list.includes(o);
          const Icon = CAT_ICONS[o] || Hammer;
          return (
            <button key={o} type="button" onClick={() => toggle(o)} className="club-tile" style={{
              flex: '1 1 calc(50% - 4px)', minWidth: 'calc(50% - 4px)', boxSizing: 'border-box',
              background: on ? 'rgba(255,204,0,0.08)' : 'var(--bg-card)',
              border: `1.5px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
              borderRadius: 15, padding: '13px 12px 12px', minHeight: 104,
              display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
              textAlign: 'left', cursor: 'pointer', position: 'relative',
              transition: 'border-color .15s, background .15s',
            }}>
              {/* чек-кружок в углу */}
              <span style={{
                position: 'absolute', top: 9, right: 9,
                width: 20, height: 20, borderRadius: '50%',
                border: `1.5px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                background: on ? 'var(--accent)' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all .15s',
              }}>
                {on && <InlineCheck size={11} width={3.4} />}
              </span>
              <IconBadge accent={on} size={36} iconSize={20}>
                <Icon />
              </IconBadge>
              <span style={{
                fontSize: 12.5, fontWeight: 700, lineHeight: 1.25, marginTop: 9,
                color: on ? 'var(--text-primary)' : 'var(--text-secondary)',
              }}>{o}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Тег-ввод услуг: пишете услугу, запятая/Enter → жёлтый тег (как на карточке).
// Под полем — подсказки по ветке, тап — добавить.
function TagInput({ tags, onChange, suggestions = [], placeholder = '' }) {
  const [input, setInput] = useState('');
  const list = Array.isArray(tags) ? tags : [];
  const commit = (raw) => {
    const parts = String(raw).split(',').map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return;
    const next = [...list];
    parts.forEach((p) => {
      if (!next.some((t) => t.toLowerCase() === p.toLowerCase())) next.push(p);
    });
    onChange(next);
  };
  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit(input); setInput(''); }
    else if (e.key === 'Backspace' && !input && list.length) onChange(list.slice(0, -1));
  };
  const free = suggestions.filter((s) => !list.some((t) => t.toLowerCase() === s.toLowerCase()));
  return (
    <div>
      <div style={{
        ...inputStyle, display: 'flex', flexWrap: 'wrap', alignItems: 'center',
        gap: 6, padding: '9px 10px', minHeight: 48, marginBottom: 0,
      }}>
        {list.map((t) => (
          <span key={t} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'var(--accent)', color: '#0D0D0D', borderRadius: 999,
            padding: '5px 8px 5px 11px', fontSize: 13, fontWeight: 700,
          }}>
            {t}
            <span onClick={() => onChange(list.filter((x) => x !== t))} style={{
              cursor: 'pointer', width: 16, height: 16, borderRadius: '50%',
              background: 'rgba(0,0,0,0.18)', display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center', fontSize: 10, lineHeight: 1,
            }}>✕</span>
          </span>
        ))}
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKeyDown}
          onBlur={() => { if (input.trim()) { commit(input); setInput(''); } }}
          onFocus={liftField} enterKeyHint="done"
          placeholder={list.length ? '' : placeholder}
          style={{
            flex: 1, minWidth: 120, border: 'none', outline: 'none',
            background: 'transparent', color: 'var(--text-primary)', fontSize: 15, padding: '4px 2px',
          }} />
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: '6px 0 8px', lineHeight: 1.4 }}>
        Пишите через запятую или Enter — каждая услуга станет тегом на карточке.
      </p>
      {free.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {free.map((s) => (
            <button key={s} type="button" onClick={() => commit(s)} style={{
              padding: '6px 11px', borderRadius: 999, fontSize: 12.5, fontWeight: 600,
              background: 'var(--bg-elevated)', color: 'var(--text-secondary)',
              border: '1px dashed var(--border)', cursor: 'pointer',
            }}>+ {s}</button>
          ))}
        </div>
      )}
    </div>
  );
}

// Фото-загрузчик с превью: по одной или пачкой, счётчик «N из max», крестик-удаление,
// статус на каждой миниатюре (крутилка → галочка). Загрузка на сервер — этап бэкенда;
// грузит батч на POST /api/card-application/upload, превью локальные (URL.createObjectURL),
// по ответу проставляет serverUrl и отдаёт список url наверх через onUploaded.
function PhotoUploader({ fieldId, max = 10, accept = 'image/*', initialUrls, onCount, onUploaded }) {
  // Уже загруженные файлы (из черновика/анкеты) восстанавливаем как готовые.
  const [items, setItems] = useState(() =>
    (Array.isArray(initialUrls) ? initialUrls : []).filter(Boolean).slice(0, max).map((u, i) => ({
      key: `init-${i}-${u}`, url: u, serverUrl: u, status: 'done',
    }))
  );
  const inputRef = useRef(null);

  useEffect(() => { if (onCount) onCount(items.length); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Отдать наверх актуальный список серверных url (только успешно загруженные).
  const emitUploaded = (list) => {
    if (onUploaded) onUploaded(list.filter((p) => p.status === 'done' && p.serverUrl).map((p) => p.serverUrl));
  };

  const addFiles = async (fileList) => {
    const room = max - items.length;
    const files = Array.from(fileList || []).slice(0, room);
    if (!files.length) return;
    const added = files.map((f, i) => ({
      key: `${f.name}-${f.size}-${i}-${f.lastModified || ''}`,
      url: URL.createObjectURL(f), serverUrl: null, status: 'uploading', file: f,
    }));
    setItems((prev) => { const next = [...prev, ...added]; if (onCount) onCount(next.length); return next; });

    // Грузим этот батч на сервер; ответ отдаёт url в порядке файлов батча.
    const fd = new FormData();
    for (const it of added) fd.append(fieldId, it.file);
    try {
      const resp = await uploadCardFiles(fd);
      const urls = fieldId === 'logo' ? (resp.logo ? [resp.logo] : []) : (resp.gallery || []);
      setItems((prev) => {
        let ui = 0;
        const next = prev.map((p) => {
          if (added.some((a) => a.key === p.key) && p.status === 'uploading') {
            const su = urls[ui++] || null;
            return { ...p, status: su ? 'done' : 'error', serverUrl: su };
          }
          return p;
        });
        emitUploaded(next);
        return next;
      });
    } catch (e) {
      setItems((prev) => {
        const next = prev.map((p) => (added.some((a) => a.key === p.key) ? { ...p, status: 'error' } : p));
        emitUploaded(next);
        return next;
      });
    }
  };

  const remove = (key) => setItems((prev) => {
    const it = prev.find((p) => p.key === key);
    if (it && it.url && it.url.startsWith('blob:')) { try { URL.revokeObjectURL(it.url); } catch (e) { /* noop */ } }
    const next = prev.filter((p) => p.key !== key);
    if (onCount) onCount(next.length);
    emitUploaded(next);
    return next;
  });

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        {items.map((it) => (
          <div key={it.key} style={{
            position: 'relative', aspectRatio: '1/1', borderRadius: 12, overflow: 'hidden',
            border: '1px solid var(--border)', background: 'var(--bg-elevated)',
          }}>
            <img src={it.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            {it.status === 'uploading' && (
              <div style={{
                position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <span className="pp-upspin" />
              </div>
            )}
            {it.status === 'done' && (
              <span style={{
                position: 'absolute', left: 6, bottom: 6, width: 20, height: 20, borderRadius: '50%',
                background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <InlineCheck size={11} width={3.5} />
              </span>
            )}
            {it.status === 'error' && (
              <div style={{
                position: 'absolute', inset: 0, background: 'rgba(170,32,32,0.42)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 10.5, fontWeight: 700, textAlign: 'center', padding: 4, lineHeight: 1.25,
              }}>Ошибка<br />загрузки</div>
            )}
            <span onClick={() => remove(it.key)} style={{
              position: 'absolute', top: 5, right: 5, width: 22, height: 22, borderRadius: '50%',
              background: 'rgba(0,0,0,0.6)', color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, lineHeight: 1,
            }}>✕</span>
          </div>
        ))}
        {items.length < max && (
          <div onClick={() => inputRef.current && inputRef.current.click()} style={{
            aspectRatio: '1/1', borderRadius: 12, cursor: 'pointer',
            border: '1.5px dashed var(--border)', background: 'var(--bg-card)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
          }}>
            <span style={{ fontSize: 22, color: 'var(--accent)', lineHeight: 1 }}>+</span>
            <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Добавить</span>
          </div>
        )}
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: '7px 0 0' }}>
        {items.length} из {max} · можно добавлять по одной или сразу несколько
      </p>
      <input ref={inputRef} type="file" accept={accept} multiple={max > 1}
        onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
        style={{ display: 'none' }} />
    </div>
  );
}

// ─── Слайд 3: анкета карточки — 12 лёгких экранов с ветвлением по типу фирмы ──
function RegistrationSlide({ animKey, slideId, active, registerBack, onNext, forceRegistration = false }) {
  const draft = useMemo(() => loadDraft(), []);
  const hasDraft = !!(draft.values && Object.keys(draft.values).length);
  const [mode, setMode] = useState('intro');   // 'intro' | 'form'
  const [step, setStep] = useState(Math.min(draft.step || 0, SCREENS.length - 1));
  const [stepKey, setStepKey] = useState(0);    // перезапуск анимации входа экрана
  const [values, setValues] = useState(draft.values || {});
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitErr, setSubmitErr] = useState('');
  const [dontShow, setDontShow] = useState(() => isSlideHidden(slideId));

  const br = branchOf(values);
  const setValue = (id, val) => setValues((prev) => ({ ...prev, [id]: val }));
  const toggleDontShow = () => setDontShow((v) => {
    const nv = !v; setSlideHidden(slideId, nv); return nv;
  });

  // Префилл из дооплатной воронки (профиль в БД) + Telegram — только в пустые поля.
  useEffect(() => {
    let cancelled = false;
    getOnboardingProfile().then((p) => {
      if (cancelled) return;
      const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
      const person = [p.first_name || tgUser?.first_name, p.last_name || tgUser?.last_name]
        .filter(Boolean).join(' ');
      setValues((prev) => {
        const next = { ...prev };
        const fill = (id, val) => {
          if (val && (next[id] == null || String(next[id]).trim() === '')) next[id] = val;
        };
        fill('phone', (p.phone && p.phone !== 'shared_via_telegram') ? p.phone : '');
        fill('email', p.email);
        fill('responsiblePerson', person);
        fill('city', p.region);
        fill('workRegions', p.region);
        return next;
      });
    }).catch(() => { /* без префилла форма всё равно работает */ });
    return () => { cancelled = true; };
  }, []);

  // Гидрация с сервера: на новом устройстве локального черновика нет → подтянуть из профиля.
  // Плюс кэшируем флаги «скрытые слайды» в localStorage (сработают на следующем входе).
  useEffect(() => {
    let cancelled = false;
    getCardApplication().then((r) => {
      if (cancelled || !r) return;
      applyHiddenFromServer(r.hidden);
      const d = r.draft;
      if (!hasDraft && d && d.values && Object.keys(d.values).length) {
        setValues((prev) => ({ ...d.values, ...prev })); // локальные/префилл-значения приоритетнее
        setStep((s) => (s ? s : Math.min(d.step || 0, SCREENS.length - 1)));
        saveDraft({ values: d.values, step: d.step || 0 });
      }
    }).catch(() => { /* нет анкеты/сети — форма работает с локальным черновиком */ });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Автосохранение черновика: localStorage мгновенно + бэкенд-зеркало с дебаунсом
  // (1.2с тишины) — свернул Telegram / сменил устройство → продолжит с того же экрана.
  useEffect(() => {
    if (submitted) return;
    saveDraft({ values, step });
    const t = setTimeout(() => {
      try { saveCardDraft({ values, step }).catch(() => {}); } catch { /* noop */ }
    }, 1200);
    return () => clearTimeout(t);
  }, [values, step, submitted]);

  const total = SCREENS.length;
  const screenId = SCREENS[step];
  const meta = screenMeta(screenId, br);
  const miss = screenMissing(screenId, values);
  const missingAll = allMissing(values);
  const canSubmit = missingAll.length === 0 && values.publishConsent === true && !busy;

  const goToStep = (s) => { setStep(Math.max(0, Math.min(s, total - 1))); setStepKey((k) => k + 1); };
  const openForm = () => { setMode('form'); setStepKey((k) => k + 1); };

  // Выбор типа: тап → сохранить (сбросив зависящие от ветки чипы) → авто-переход.
  const pickType = (key) => {
    setValues((prev) => (prev.companyType === key ? prev : {
      ...prev, companyType: key, profileTag: [], profileTag__custom: '', categories: [],
    }));
    setTimeout(() => goToStep(1), 250);
  };

  const doSubmit = async () => {
    if (!canSubmit) return;
    setBusy(true); setSubmitErr('');
    try { await submitRegistration(values); clearDraft(); setSubmitted(true); }
    catch (e) { setSubmitErr(e?.message || 'Не удалось отправить анкету. Проверьте связь и попробуйте ещё раз.'); }
    finally { setBusy(false); }
  };

  // Кнопка «Назад» в shell делегируется сюда: отматываем экраны / выходим в интро.
  useEffect(() => {
    if (!active) return;
    registerBack(() => {
      if (mode === 'form' && !submitted) {
        if (step > 0) { goToStep(step - 1); return true; }
        setMode('intro'); return true;
      }
      return false; // интро / после отправки → shell уводит на предыдущий слайд
    });
    return () => registerBack(null);
  }, [active, mode, step, submitted, registerBack]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── ИНТРО ──
  // Компактная вёрстка: превью карточки — невысокая «шапка» с фейдом (не съедает экран),
  // CTA-кнопка видна сразу без скролла и пульсирует — понятно, что жать.
  if (mode === 'intro') {
    return (
      <div style={{ width: '100%', maxWidth: 400 }}>
        <style>{`
          @keyframes ppCtaPulse {
            0%, 100% { box-shadow: 0 6px 20px rgba(255,204,0,0.24); transform: scale(1); }
            50% { box-shadow: 0 8px 30px rgba(255,204,0,0.55); transform: scale(1.015); }
          }
          @media (prefers-reduced-motion: reduce) { .pp-cta-pulse { animation: none !important; } }
        `}</style>

        <div key={`r1-${animKey}`} className="pp-anim-1" style={{
          position: 'relative', height: 148, overflow: 'hidden',
          borderRadius: 16, border: '1px solid var(--border)', marginBottom: 16,
        }}>
          {PLATFORM_SCREENSHOT_URL ? (
            <img src={PLATFORM_SCREENSHOT_URL} alt="fix-hub.de"
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center', display: 'block' }} />
          ) : (
            <div style={{
              width: '100%', height: '100%', background: 'var(--bg-card)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--text-tertiary)', fontSize: 13,
            }}>🖥️ Карточка на fix-hub.de</div>
          )}
          {/* Фейд снизу: это превью, а не контент для изучения */}
          <div style={{
            position: 'absolute', left: 0, right: 0, bottom: 0, height: 64,
            background: 'linear-gradient(to bottom, transparent, var(--bg-primary))',
            pointerEvents: 'none',
          }} />
          <span style={{
            position: 'absolute', left: '50%', bottom: 8, transform: 'translateX(-50%)',
            padding: '4px 11px', borderRadius: 999, whiteSpace: 'nowrap',
            background: 'rgba(20,20,22,0.72)', border: '1px solid var(--border)',
            backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
            fontSize: 11.5, fontWeight: 600, color: 'var(--text-secondary)',
          }}>Пример готовой карточки</span>
        </div>

        <h2 key={`r2-${animKey}`} className="pp-anim-2" style={{
          fontSize: 21, fontWeight: 800, textAlign: 'center', margin: '0 0 8px',
          color: 'var(--text-primary)', lineHeight: 1.3, letterSpacing: '-0.3px',
        }}>
          Ваша карточка на fix-hub.de
        </h2>
        <p key={`r3-${animKey}`} className="pp-anim-3" style={{
          fontSize: 14.5, color: 'var(--text-secondary)', lineHeight: 1.55, textAlign: 'center', margin: '0 0 18px',
        }}>
          Немецкие заказчики находят вас в каталоге напрямую — 0% комиссий.
          Анкета займёт ~3 минуты, дальше карточку соберём мы.
        </p>

        <div key={`r5-${animKey}`} className="pp-anim-4">
          <button onClick={openForm} className="pp-cta-pulse" style={{
            width: '100%', padding: '16px 0', background: 'var(--accent)', color: '#0D0D0D',
            border: 'none', borderRadius: 14, fontSize: 16, fontWeight: 700, cursor: 'pointer',
            animation: 'ppCtaPulse 2.2s ease-in-out 0.8s infinite',
          }}>
            {hasDraft ? 'Продолжить заполнение →' : 'Заполнить анкету →'}
          </button>
          <p style={{
            fontSize: 12.5, color: 'var(--text-tertiary)', lineHeight: 1.5, textAlign: 'center', margin: '10px 0 0',
          }}>
            Часть ответов уже подставлена — останется проверить и дополнить.
          </p>
          <button onClick={onNext} style={{
            width: '100%', padding: '11px 0', marginTop: 6, background: 'transparent',
            color: 'var(--text-tertiary)', border: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}>
            Позже
          </button>
        </div>

        {/* В режиме баннера/деп-линка галочку не даём: баннер горит до отправки анкеты. */}
        {!forceRegistration && <DontShowRow checked={dontShow} onToggle={toggleDontShow} />}
      </div>
    );
  }

  // ── ПОСЛЕ ОТПРАВКИ ──
  if (submitted) {
    return (
      <div style={{ width: '100%', maxWidth: 400, textAlign: 'center' }}>
        <div className="pp-anim-1" style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
          <span style={{
            width: 72, height: 72, borderRadius: '50%',
            background: 'radial-gradient(circle at 50% 38%, #FFE27A, var(--accent) 72%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}><InlineCheck size={34} width={3} /></span>
        </div>
        <h2 className="pp-anim-2" style={{ fontSize: 21, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 10px' }}>
          Анкета отправлена
        </h2>
        <p className="pp-anim-3" style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 24px' }}>
          Спасибо! Мы соберём вашу карточку и напишем, когда она появится на платформе.
        </p>
        <button onClick={onNext} className="pp-anim-4" style={{
          width: '100%', padding: '16px 0',
          background: 'var(--accent)', color: '#0D0D0D',
          border: 'none', borderRadius: 14, fontSize: 16, fontWeight: 700, cursor: 'pointer',
          boxShadow: '0 6px 20px rgba(255,204,0,0.28)',
        }}>
          Далее →
        </button>
      </div>
    );
  }

  // ── КОНТЕНТ ЭКРАНОВ ──
  const isOptional = screenId === 'diff' || screenId === 'media' || screenId === 'links';
  const OPT_KEYS = {
    diff: ['differentiation', 'certifications'],
    media: ['youtubeLink', 'logoCount', 'galleryCount'],
    links: ['website', 'socialInstagram', 'socialFacebook', 'socialTiktok', 'socialYoutube', 'socialWhatsapp', 'openingHours'],
  };
  const optionalEmpty = isOptional && OPT_KEYS[screenId].every((k) => {
    const v = values[k];
    return v == null || v === '' || v === 0;
  });

  // Полная сводка заполненного — чтобы человек проверил ВСЁ, что ввёл, перед отправкой.
  // Полная сводка ВСЕХ заполненных полей формы — чтобы человек перепроверил всё, что ввёл.
  const arrJoin = (v) => (Array.isArray(v) ? v.filter(Boolean).join(', ') : (v || ''));
  const reviewRows = screenId !== 'review' ? [] : [
    ['Тип', values.companyType ? `${br.icon} ${br.title}` : ''],
    ['Фирма', values.companyName],
    ['Торговое название', values.tradeName],
    ['Контактное лицо', values.responsiblePerson],
    ['Телефон', values.phone],
    ['E-mail', values.email],
    ['Адрес на карточке', cardAddress(values)],
    ['Город', values.city],
    ['Орг-форма', values.legalForm],
    ['Gewerbeanmeldung', values.gewerbeanmeldung],
    ['USt-IdNr', values.ustId],
    ['Handelsregister', values.handelsregister],
    ['Специализация', specSummary(values)],
    ['Категории', arrJoin(values.categories)],
    ['Услуги', arrJoin(values.services)],
    ['Описание', values.companyDescription],
    ['Год основания', values.foundedYear],
    ['Чем отличаетесь', values.differentiation],
    ['Сертификаты / лицензии', values.certifications],
    [br.regionsLabel || 'Регионы работы', values.workRegions],
    ['Часы работы', values.openingHours],
    ['Сайт', values.website],
    ['Instagram', values.socialInstagram],
    ['Facebook', values.socialFacebook],
    ['TikTok', values.socialTiktok],
    ['YouTube', values.socialYoutube],
    ['WhatsApp', values.socialWhatsapp],
    ['Видео о фирме', values.youtubeLink],
    ['Логотип', values.logo ? 'загружен ✓' : ''],
    ['Фото', values.galleryCount ? `${values.galleryCount} шт` : ''],
  ].filter(([, v]) => v && String(v).trim());

  const addOnlineRegion = () => {
    const cur = str(values.workRegions);
    const tag = 'Онлайн — вся Германия';
    if (cur.toLowerCase().includes('онлайн')) return;
    setValue('workRegions', cur ? `${cur}, ${tag}` : tag);
  };

  return (
    <div style={{ width: '100%', maxWidth: 400 }}>
      {/* Прогресс «Экран N из 12» + кликабельные полоски */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>
            {step + 1} из {total}
          </span>
          {isOptional && (
            <span style={{ fontSize: 12, color: 'var(--text-tertiary)', fontWeight: 600 }}>по желанию</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 3 }}>
          {SCREENS.map((id, i) => (
            <div key={id} onClick={() => goToStep(i)} style={{
              flex: 1, height: 4, borderRadius: 2, cursor: 'pointer',
              background: i === step ? 'var(--accent)'
                : i < step
                  ? (screenMissing(id, values).length ? 'rgba(255,69,58,0.5)' : 'rgba(255,212,59,0.45)')
                  : 'var(--bg-elevated)',
              transition: 'background .3s',
            }} />
          ))}
        </div>
      </div>

      <div key={stepKey} className="pp-anim-2">
        <h2 style={{
          fontSize: 20, fontWeight: 800, color: 'var(--text-primary)',
          margin: '4px 0 6px', lineHeight: 1.3, letterSpacing: '-0.2px',
        }}>
          {meta.title}
        </h2>
        <p style={{ fontSize: 13, color: 'var(--text-tertiary)', margin: '0 0 16px', lineHeight: 1.45 }}>
          {meta.note}
        </p>

        {/* 1 · Тип фирмы — 4 плитки с объяснением «для кого» */}
        {screenId === 'type' && (
          <div>
            {Object.entries(BRANCHES).map(([key, b]) => {
              const on = values.companyType === key;
              return (
                <div key={key} onClick={() => pickType(key)} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 14px',
                  borderRadius: 14, cursor: 'pointer', marginBottom: 9,
                  background: on ? 'rgba(255,204,0,0.08)' : 'var(--bg-card)',
                  border: `1.5px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                  transition: 'border-color .15s, background .15s',
                }}>
                  <span style={{ fontSize: 26, flexShrink: 0, lineHeight: 1.1 }}>{b.icon}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3 }}>
                      {b.title}
                    </div>
                    <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', lineHeight: 1.4, marginTop: 3 }}>
                      {b.desc}
                    </div>
                  </div>
                  {on && (
                    <span style={{ flexShrink: 0, marginTop: 2 }}>
                      <InlineCheck size={16} color="var(--accent)" width={3} />
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* 2 · Контакты (префилл) */}
        {screenId === 'contacts' && (
          <div>
            <p style={{ fontSize: 12.5, color: 'var(--accent)', fontWeight: 600, margin: '-6px 0 14px' }}>
              ⚡ Уже подставлено из ваших ответов — просто проверьте.
            </p>
            <Field label="Телефон" required hint="Этот номер увидят клиенты. Можно указать другой.">
              <TextInput type="tel" value={values.phone} onChange={(v) => setValue('phone', v)} placeholder="+49 …" />
            </Field>
            <Field label="E-mail" required>
              <TextInput type="email" value={values.email} onChange={(v) => setValue('email', v)} placeholder="info@firma.de" />
            </Field>
            <Field label="Контактное лицо" required>
              <TextInput value={values.responsiblePerson} onChange={(v) => setValue('responsiblePerson', v)} placeholder="Имя и Фамилия" />
            </Field>
          </div>
        )}

        {/* 3 · Адрес: юридический + фактический + выбор для карточки */}
        {screenId === 'address' && (
          <div>
            <Field label="Юридический адрес" required hint="Адрес, на который зарегистрирована фирма.">
              <TextInput value={values.addressLegal} onChange={(v) => setValue('addressLegal', v)}
                placeholder="Musterstraße 11, 85540 Haar" />
            </Field>
            <GateCheckbox
              checked={values.addressSameAsLegal !== false}
              onToggle={() => setValue('addressSameAsLegal', values.addressSameAsLegal === false)}
              label="Фактический адрес совпадает с юридическим"
            />
            {values.addressSameAsLegal === false && (
              <>
                <Field label="Фактический адрес" required hint="Где реально находится офис, склад или магазин.">
                  <TextInput value={values.addressActual} onChange={(v) => setValue('addressActual', v)}
                    placeholder="Beispielweg 5, 80331 München" />
                </Field>
                <Field label="Какой адрес показать на карточке?" required>
                  <div>
                    <RadioRow checked={values.addressShow === 'legal'} label="Юридический"
                      onPick={() => setValue('addressShow', 'legal')} />
                    <RadioRow checked={values.addressShow === 'actual'} label="Фактический"
                      onPick={() => setValue('addressShow', 'actual')} />
                  </div>
                </Field>
              </>
            )}
            <Field label="Основной город работы" required>
              <TextInput value={values.city} onChange={(v) => setValue('city', v)} placeholder="Например: München" />
            </Field>
          </div>
        )}

        {/* 4 · Юридическая информация */}
        {screenId === 'legal' && (
          <div>
            <Field label="Организационно-правовая форма" required>
              <SelectInput value={values.legalForm} onChange={(v) => setValue('legalForm', v)}
                options={['GmbH', 'Einzelunternehmen (Gewerbe)', 'UG', 'GbR', 'Freiberufler', 'Другое']} />
            </Field>
            <Field label="Gewerbeanmeldung">
              <TextInput value={values.gewerbeanmeldung} onChange={(v) => setValue('gewerbeanmeldung', v)} />
            </Field>
            <Field label="Umsatzsteuer-ID">
              <TextInput value={values.ustId} onChange={(v) => setValue('ustId', v)} placeholder="DE…" />
            </Field>
            <Field label="Handelsregister" hint="Для GmbH / UG.">
              <TextInput value={values.handelsregister} onChange={(v) => setValue('handelsregister', v)} placeholder="HRB …" />
            </Field>
            <Field label="Коммерческое название" hint="Если отличается от официального.">
              <TextInput value={values.tradeName} onChange={(v) => setValue('tradeName', v)} />
            </Field>
          </div>
        )}

        {/* 5 · Название и специализация */}
        {screenId === 'name' && (
          <div>
            <Field label="Название фирмы" required>
              <TextInput value={values.companyName} onChange={(v) => setValue('companyName', v)} placeholder={br.namePh} />
            </Field>
            <Field label="Специализация" required hint="Выберите подходящее — или впишите свою.">
              <div>
                <ChipsPicker options={br.specChips} sel={values.profileTag}
                  onChange={(arr) => setValue('profileTag', arr)} />
                <input className="pp-input" type="text" placeholder="Свой вариант…"
                  value={values.profileTag__custom || ''} onFocus={liftField}
                  onChange={(e) => setValue('profileTag__custom', e.target.value)}
                  style={{ ...inputStyle, marginTop: 10, marginBottom: 0 }} />
              </div>
            </Field>
          </div>
        )}

        {/* 6 · Описание */}
        {screenId === 'about' && (
          <div>
            <Field label="Коротко о фирме" required hint="2–3 предложения: что делаете, для кого, чем сильны.">
              <TextInput rows={4} value={values.companyDescription}
                onChange={(v) => setValue('companyDescription', v)} placeholder={br.descPh} />
            </Field>
            <Field label="Год основания" hint="На карточке будет: «на рынке с 2017».">
              <TextInput type="number" value={values.foundedYear}
                onChange={(v) => setValue('foundedYear', v)} placeholder="Например: 2017" />
            </Field>
          </div>
        )}

        {/* 7 · Категории каталога (по ветке, без лимита) — плитки 2 в ряд с SVG-иконками */}
        {screenId === 'categories' && (
          <CategoryPicker options={br.categories} sel={values.categories}
            onChange={(arr) => setValue('categories', arr)} />
        )}

        {/* 8 · Услуги (тег-ввод) + регионы */}
        {screenId === 'services' && (
          <div>
            <Field label="Перечень услуг" required>
              <TagInput tags={values.services} onChange={(arr) => setValue('services', arr)}
                suggestions={br.serviceSuggest} placeholder={br.servicePh} />
            </Field>
            <Field label={br.regionsLabel} required hint="Подставлено из ваших ответов — дополните при необходимости.">
              <div>
                <TextInput value={values.workRegions} onChange={(v) => setValue('workRegions', v)}
                  placeholder={br.regionsPh} />
                {br.onlineChip && (
                  <button type="button" onClick={addOnlineRegion} style={{
                    marginTop: 8, padding: '6px 11px', borderRadius: 999, fontSize: 12.5, fontWeight: 600,
                    background: 'var(--bg-elevated)', color: 'var(--text-secondary)',
                    border: '1px dashed var(--border)', cursor: 'pointer',
                  }}>+ Онлайн — вся Германия</button>
                )}
              </div>
            </Field>
          </div>
        )}

        {/* 9 · Отличия (по желанию) */}
        {screenId === 'diff' && (
          <div>
            <Field label="Чем отличаетесь от других?">
              <TextInput rows={3} value={values.differentiation}
                onChange={(v) => setValue('differentiation', v)}
                placeholder={br.diffPh} />
            </Field>
            <Field label="Сертификаты и квалификации">
              <TextInput rows={2} value={values.certifications}
                onChange={(v) => setValue('certifications', v)}
                placeholder={br.certPh} />
            </Field>
          </div>
        )}

        {/* 10 · Логотип и фото */}
        {screenId === 'media' && (
          <div>
            <Field label="Логотип">
              <PhotoUploader fieldId="logo" max={1} accept=".png,.svg,.jpeg,.jpg,image/*"
                initialUrls={values.logo ? [values.logo] : []}
                onCount={(n) => setValue('logoCount', n)}
                onUploaded={(urls) => setValue('logo', urls[0] || '')} />
            </Field>
            <Field label="Фото (до 10)">
              <PhotoUploader fieldId="gallery" max={10} accept="image/*"
                initialUrls={Array.isArray(values.gallery) ? values.gallery : []}
                onCount={(n) => setValue('galleryCount', n)}
                onUploaded={(urls) => setValue('gallery', urls)} />
            </Field>
            <Field label="Видео о фирме (YouTube)" hint="Ссылка на ролик — покажем его на карточке.">
              <TextInput type="url" value={values.youtubeLink}
                onChange={(v) => setValue('youtubeLink', v)} placeholder="youtube.com/…" />
            </Field>
          </div>
        )}

        {/* 11 · Сайт и соцсети */}
        {screenId === 'links' && (
          <div>
            <Field label="🌐 Сайт" hint="Впишите ваш сайт, если есть.">
              <TextInput type="url" value={values.website} onChange={(v) => setValue('website', v)}
                placeholder="www.ihrefirma.de" />
            </Field>
            <Field label="📷 Instagram">
              <TextInput value={values.socialInstagram} onChange={(v) => setValue('socialInstagram', v)}
                placeholder="instagram.com/ihrefirma" />
            </Field>
            <Field label="📘 Facebook">
              <TextInput value={values.socialFacebook} onChange={(v) => setValue('socialFacebook', v)}
                placeholder="facebook.com/ihrefirma" />
            </Field>
            <Field label="🎵 TikTok">
              <TextInput value={values.socialTiktok} onChange={(v) => setValue('socialTiktok', v)}
                placeholder="tiktok.com/@ihrefirma" />
            </Field>
            <Field label="▶️ YouTube">
              <TextInput value={values.socialYoutube} onChange={(v) => setValue('socialYoutube', v)}
                placeholder="youtube.com/@ihrefirma" />
            </Field>
            <Field label="💬 WhatsApp">
              <TextInput value={values.socialWhatsapp} onChange={(v) => setValue('socialWhatsapp', v)}
                placeholder="+49 176 … или wa.me/…" />
            </Field>
            <Field label="🕐 Часы работы">
              <SelectInput value={values.openingHours} onChange={(v) => setValue('openingHours', v)}
                placeholderOption="Не указывать"
                options={['Mo–Fr 08:00–18:00', 'Mo–Sa 08:00–18:00', 'Mo–So 08:00–20:00', 'Nach Vereinbarung (по договорённости)']} />
            </Field>
          </div>
        )}

        {/* 12 · Сводка и отправка */}
        {screenId === 'review' && (
          <div>
            <div style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 14, padding: '4px 16px', marginBottom: 14,
            }}>
              {reviewRows.length === 0 && (
                <p style={{ fontSize: 13.5, color: 'var(--text-tertiary)', padding: '10px 0' }}>
                  Пока пусто — вернитесь к предыдущим экранам.
                </p>
              )}
              {reviewRows.map(([label, val]) => (
                <div key={label} style={{
                  display: 'flex', gap: 10, padding: '9px 0',
                  borderBottom: '1px solid var(--border)',
                }}>
                  <span style={{ fontSize: 12.5, color: 'var(--text-tertiary)', width: 112, flexShrink: 0, paddingTop: 1 }}>{label}</span>
                  <span style={{ fontSize: 13.5, color: 'var(--text-primary)', fontWeight: 600, lineHeight: 1.4, wordBreak: 'break-word' }}>{val}</span>
                </div>
              ))}
            </div>

            {missingAll.length > 0 && (
              <div style={{
                background: 'rgba(255,69,58,0.07)', border: '1px solid rgba(255,69,58,0.35)',
                borderRadius: 12, padding: '10px 14px', marginBottom: 14,
              }}>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 7 }}>Не хватает — нажмите, чтобы заполнить:</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {missingAll.map((m) => (
                    <button key={m.label} type="button" onClick={() => goToStep(m.screen)} style={{
                      padding: '5px 11px', borderRadius: 999, fontSize: 12.5, fontWeight: 600,
                      background: 'var(--bg-elevated)', color: 'var(--text-primary)',
                      border: '1px solid rgba(255,69,58,0.45)', cursor: 'pointer',
                    }}>{m.label} →</button>
                  ))}
                </div>
              </div>
            )}

            <GateCheckbox
              checked={values.publishConsent === true}
              onToggle={() => setValue('publishConsent', values.publishConsent !== true)}
              label="Я проверил(а) — все данные верны, и согласен(на) на их публикацию в карточке моей фирмы на fix-hub.de"
            />
          </div>
        )}
      </div>

      {/* Нижняя кнопка: Далее / Пропустить / Отправить */}
      {screenId !== 'review' ? (
        <>
          <button onClick={() => miss.length === 0 && goToStep(step + 1)} disabled={miss.length > 0} style={{
            width: '100%', padding: '16px 0',
            background: miss.length === 0 ? 'var(--accent)' : 'var(--bg-elevated)',
            color: miss.length === 0 ? '#0D0D0D' : 'var(--text-tertiary)',
            border: 'none', borderRadius: 14, fontSize: 16, fontWeight: 700,
            cursor: miss.length === 0 ? 'pointer' : 'default', transition: 'all .2s',
            boxShadow: miss.length === 0 ? '0 6px 20px rgba(255,204,0,0.28)' : 'none',
          }}>
            {optionalEmpty ? 'Пропустить →' : 'Далее →'}
          </button>
          {miss.length > 0 && screenId !== 'type' && (
            <p style={{ fontSize: 12.5, color: 'var(--text-tertiary)', textAlign: 'center', margin: '10px 0 0', lineHeight: 1.4 }}>
              Заполните обязательные поля (со&nbsp;*), чтобы продолжить.
            </p>
          )}
        </>
      ) : (
        <>
          {submitErr && (
            <p style={{
              margin: '0 0 10px', fontSize: 13, color: '#ff7a7a', textAlign: 'center', lineHeight: 1.4,
            }}>{submitErr}</p>
          )}
          <button onClick={doSubmit} disabled={!canSubmit} style={{
            width: '100%', padding: '16px 0',
            background: canSubmit ? 'var(--accent)' : 'var(--bg-elevated)',
            color: canSubmit ? '#0D0D0D' : 'var(--text-tertiary)',
            border: 'none', borderRadius: 14, fontSize: 16, fontWeight: 700,
            cursor: canSubmit ? 'pointer' : 'default', transition: 'all .2s',
            boxShadow: canSubmit ? '0 6px 20px rgba(255,204,0,0.28)' : 'none',
          }}>
            {busy ? 'Отправка…' : 'Отправить анкету'}
          </button>
        </>
      )}
    </div>
  );
}
